import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  User,
  UserCreateRequest,
  UserListResponse,
  UserUpdateRequest,
} from '../models/user.models';

/**
 * Thin client for the backend users API (prefix /api/v1/users).
 * Components own their own loading/error/data state; this service only
 * performs HTTP calls. See the `idio-backend-api` skill for the contract.
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = `${import.meta.env['NG_APP_API_URL'] ?? ''}/api/v1/users`;

  /** GET /users/ — `page` is 0-based; `filter` is a partial name match. */
  listUsers(page: number, limit: number, filter: string): Observable<UserListResponse> {
    let params = new HttpParams().set('page', page).set('limit', limit);
    const trimmed = filter.trim();
    if (trimmed) params = params.set('filter', trimmed);
    return this.http.get<UserListResponse>(`${this.apiBase}/`, { params });
  }

  getUser(id: string): Observable<User> {
    return this.http.get<User>(`${this.apiBase}/${id}`);
  }

  /** Requires a root account. */
  createUser(body: UserCreateRequest): Observable<User> {
    return this.http.post<User>(`${this.apiBase}/`, body);
  }

  updateUser(id: string, body: UserUpdateRequest): Observable<User> {
    return this.http.put<User>(`${this.apiBase}/${id}`, body);
  }

  /** Soft-deletes the user and returns the (deleted) record. */
  deleteUser(id: string): Observable<User> {
    return this.http.delete<User>(`${this.apiBase}/${id}`);
  }
}
